import type { ReactNode } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'

// Hand-rolled SVG chart primitives (spec §15). No chart library dependency.

export interface ChartSeries {
  label: string
  color: string
  points: number[] // % values 0-100
}

const LINE_CHART_WIDTH = 400
const GRIDLINES = [0, 25, 50, 75, 100]

export function LineChart(props: {
  series: ChartSeries[]
  xLabels: string[]
  height?: number
}): ReactNode {
  const { series, xLabels, height = 160 } = props
  const steps = Math.max(xLabels.length - 1, 1)

  const toX = (i: number) => (i / steps) * LINE_CHART_WIDTH
  const toY = (value: number) =>
    height - (Math.min(Math.max(value, 0), 100) / 100) * height

  return (
    <div className="flex flex-col gap-1.5">
      <svg
        viewBox={`0 0 ${LINE_CHART_WIDTH} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label="line chart"
        preserveAspectRatio="none"
      >
        {GRIDLINES.map((pct) => (
          <line
            key={pct}
            x1={0}
            x2={LINE_CHART_WIDTH}
            y1={toY(pct)}
            y2={toY(pct)}
            stroke="var(--cd-border)"
            strokeWidth={1}
          />
        ))}
        {series.map((s) => (
          <polyline
            key={s.label}
            points={s.points.map((v, i) => `${toX(i)},${toY(v)}`).join(' ')}
            fill="none"
            stroke={s.color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-(--cd-text-faint)">
        {xLabels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
    </div>
  )
}

export interface DonutSegment {
  label: string
  color: string
  count: number
}

// Review mocks' exact recipe (spec §15): viewBox 42, r 15.9, stroke-width 6, rotate -90deg,
// per-segment stroke-dasharray = max(frac*CIRC - 1.5, 0) CIRC with negative cumulative
// offsets, center total, hover tooltip.
const DONUT_R = 15.9
const DONUT_CIRC = 2 * Math.PI * DONUT_R

export function DonutWheel(props: {
  segments: DonutSegment[]
  caption: string
}): ReactNode {
  const { segments, caption } = props
  const total = segments.reduce((sum, s) => sum + s.count, 0)

  let acc = 0
  const dashed = segments.map((seg) => {
    const frac = total > 0 ? seg.count / total : 0
    const dash = `${Math.max(frac * DONUT_CIRC - 1.5, 0)} ${DONUT_CIRC}`
    const offset = -acc * DONUT_CIRC
    acc += frac
    return { ...seg, frac, dash, offset }
  })

  return (
    <div className="flex flex-col items-center gap-[3px]">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative" style={{ width: 50, height: 50 }}>
            <svg
              viewBox="0 0 42 42"
              width={50}
              height={50}
              style={{ transform: 'rotate(-90deg)', display: 'block' }}
              role="img"
              aria-label={caption}
            >
              <circle
                cx={21}
                cy={21}
                r={DONUT_R}
                fill="none"
                stroke="var(--cd-border)"
                strokeWidth={6}
              />
              {dashed.map((seg) => (
                <circle
                  key={seg.label}
                  cx={21}
                  cy={21}
                  r={DONUT_R}
                  fill="none"
                  stroke={seg.color}
                  strokeWidth={6}
                  strokeDasharray={seg.dash}
                  strokeDashoffset={seg.offset}
                />
              ))}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[12.5px] font-bold text-(--cd-text)">
                {total}
              </span>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col gap-[5px]">
            {dashed.map((seg) => (
              <div key={seg.label} className="flex items-center gap-1.5">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: seg.color }}
                />
                <span className="w-4 text-xs font-bold">{seg.count}</span>
                <span className="text-[11px]">{seg.label}</span>
                <span className="ml-auto pl-2.5 text-[10.5px] opacity-70">
                  {total > 0 ? `${Math.round(seg.frac * 100)}%` : '0%'}
                </span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
      <span className="text-[10px] text-(--cd-text-faint)">{caption}</span>
    </div>
  )
}

export interface BarSegment {
  label: string
  color: string
  count: number
}

export function SegmentBar(props: { segments: BarSegment[] }): ReactNode {
  const { segments } = props
  const total = segments.reduce((sum, s) => sum + s.count, 0)

  return (
    <div
      className="flex h-[7px] w-full overflow-hidden rounded-full bg-(--cd-border)"
      role="img"
      aria-label="segment bar"
    >
      {segments.map((seg, i) => {
        const pct = total > 0 ? (seg.count / total) * 100 : 0
        return (
          <div
            key={seg.label}
            data-testid={`segment-bar-item-${i}`}
            style={{ width: `${pct}%`, background: seg.color }}
          />
        )
      })}
    </div>
  )
}

function clampPercent(value: number): number {
  return Math.min(Math.max(value, 0), 100)
}

export function RiskBar(props: { percent: number; color?: string }): ReactNode {
  const { percent, color = 'var(--cd-red)' } = props
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-(--cd-border)"
      role="progressbar"
      aria-label="risk"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full"
        style={{ width: `${clampPercent(percent)}%`, background: color }}
      />
    </div>
  )
}

export function ScoreBar(props: {
  percent: number
  color: string
  marker?: number
}): ReactNode {
  const { percent, color, marker } = props
  return (
    <div
      className="relative h-1.5 w-full rounded-full bg-(--cd-border)"
      role="progressbar"
      aria-label="score"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full"
        style={{ width: `${clampPercent(percent)}%`, background: color }}
      />
      {marker != null && (
        <div
          data-testid="score-bar-marker"
          className="absolute top-0 h-full w-px bg-(--cd-text)"
          style={{ left: `${clampPercent(marker)}%` }}
        />
      )}
    </div>
  )
}
